// Input tanda tangan digital karyawan (terpisah dari enroll wajah). Dua metode:
//  1) langsung di layar (kanvas, transparan sejak awal)
//  2) unggah file foto/scan → background dihapus otomatis di browser
// Hasil selalu PNG transparan, dipotong rapat, maksimal 800 px lebar.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { ambilGambarTandaTangan, hapusTandaTangan, simpanTandaTangan } from "./tandaTanganApi";
import { hapusBackground, potongRapat, rasioTinta } from "./tandaTanganImage";

const PAPAN_W = 640;
const PAPAN_H = 240;
const MAKS_LEBAR_HASIL = 800;
const MAKS_SISI_UPLOAD = 1400;
const KOTAK = {
  backgroundColor: "#fff",
  backgroundImage:
    "linear-gradient(45deg,#e5e7eb 25%,transparent 25%),linear-gradient(-45deg,#e5e7eb 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#e5e7eb 75%),linear-gradient(-45deg,transparent 75%,#e5e7eb 75%)",
  backgroundSize: "16px 16px",
  backgroundPosition: "0 0,0 8px,8px -8px,-8px 0",
};
const WARNA_PENA = { hitam: "#111111", biru: "#14288c" };

// RGBA terpotong → Blob PNG (diperkecil bila terlalu lebar).
const keBlob = (rgba, w, h) =>
  new Promise((resolve) => {
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    c.getContext("2d").putImageData(new ImageData(rgba, w, h), 0, 0);
    if (w <= MAKS_LEBAR_HASIL) return c.toBlob(resolve, "image/png");
    const skala = MAKS_LEBAR_HASIL / w;
    const k = document.createElement("canvas");
    k.width = MAKS_LEBAR_HASIL;
    k.height = Math.max(1, Math.round(h * skala));
    const kx = k.getContext("2d");
    kx.imageSmoothingQuality = "high";
    kx.drawImage(c, 0, 0, k.width, k.height);
    k.toBlob(resolve, "image/png");
  });

const TandaTanganModal = ({ target, onClose, onSaved }) => {
  const [metode, setMetode] = useState("Gambar");
  const [saving, setSaving] = useState(false);
  const [saatIni, setSaatIni] = useState(null); // object URL tanda tangan terpasang
  const [memuat, setMemuat] = useState(!!target.TandaTangan);

  // ── metode 1: kanvas ──
  const papan = useRef(null);
  const menggambar = useRef(false);
  const titikAkhir = useRef(null);
  const [warnaPena, setWarnaPena] = useState("hitam");
  const [adaGoresan, setAdaGoresan] = useState(false);

  // ── metode 2: unggah ──
  const sumber = useRef(null); // {data, w, h} RGBA foto asli (diperkecil)
  const preview = useRef(null);
  const [namaFile, setNamaFile] = useState("");
  const [kepekaan, setKepekaan] = useState(55);
  const [warnaHasil, setWarnaHasil] = useState("asli");
  const [hasilUpload, setHasilUpload] = useState(null); // {data,width,height}
  const [peringatan, setPeringatan] = useState("");

  useEffect(() => {
    let batal = false;
    let url = null;
    if (target.TandaTangan) {
      ambilGambarTandaTangan(target.Nik).then((u) => {
        if (batal) {
          if (u) URL.revokeObjectURL(u);
          return;
        }
        url = u;
        setSaatIni(u);
        setMemuat(false);
      });
    }
    return () => {
      batal = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [target.Nik, target.TandaTangan]);

  // kanvas: skala piksel perangkat agar goresan halus
  useEffect(() => {
    const c = papan.current;
    if (!c || metode !== "Gambar") return;
    const dpr = window.devicePixelRatio || 1;
    c.width = PAPAN_W * dpr;
    c.height = PAPAN_H * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    setAdaGoresan(false);
  }, [metode]);

  const posisi = (e) => {
    const r = papan.current.getBoundingClientRect();
    return { x: ((e.clientX - r.left) * PAPAN_W) / r.width, y: ((e.clientY - r.top) * PAPAN_H) / r.height };
  };
  const mulai = (e) => {
    e.preventDefault();
    papan.current.setPointerCapture(e.pointerId);
    menggambar.current = true;
    titikAkhir.current = posisi(e);
    const ctx = papan.current.getContext("2d");
    ctx.fillStyle = WARNA_PENA[warnaPena];
    ctx.beginPath();
    ctx.arc(titikAkhir.current.x, titikAkhir.current.y, 1.3, 0, Math.PI * 2);
    ctx.fill();
    setAdaGoresan(true);
  };
  const gerak = (e) => {
    if (!menggambar.current) return;
    const p = posisi(e);
    const a = titikAkhir.current;
    const ctx = papan.current.getContext("2d");
    ctx.strokeStyle = WARNA_PENA[warnaPena];
    // lebar mengikuti kecepatan (lebih tipis saat cepat) → goresan alami
    const jarak = Math.hypot(p.x - a.x, p.y - a.y);
    ctx.lineWidth = Math.max(1.4, 3.2 - jarak * 0.12);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.quadraticCurveTo(a.x, a.y, (a.x + p.x) / 2, (a.y + p.y) / 2);
    ctx.stroke();
    titikAkhir.current = p;
  };
  const selesai = () => {
    menggambar.current = false;
  };
  const bersihkanPapan = () => {
    const c = papan.current;
    c.getContext("2d").clearRect(0, 0, PAPAN_W, PAPAN_H);
    setAdaGoresan(false);
  };

  // ── unggah: baca foto, hapus background (ulang saat pengaturan berubah) ──
  const prosesUpload = useCallback(() => {
    const s = sumber.current;
    if (!s) return;
    const out = hapusBackground(s.data, s.w, s.h, { kepekaan, warna: warnaHasil });
    const potong = potongRapat(out, s.w, s.h);
    if (!potong) {
      setHasilUpload(null);
      setPeringatan("Tidak ada goresan yang terdeteksi — naikkan kepekaan atau coba foto yang lebih jelas.");
      return;
    }
    const rasio = rasioTinta(potong.data, potong.width, potong.height);
    setPeringatan(
      rasio > 0.45
        ? "Hasil terlalu penuh — turunkan kepekaan agar noda/bayangan ikut hilang."
        : rasio < 0.004
        ? "Goresan sangat tipis — naikkan kepekaan."
        : ""
    );
    setHasilUpload(potong);
  }, [kepekaan, warnaHasil]);

  useEffect(() => {
    if (metode !== "Upload" || !sumber.current) return;
    const t = setTimeout(prosesUpload, 120);
    return () => clearTimeout(t);
  }, [kepekaan, warnaHasil, metode, prosesUpload]);

  useEffect(() => {
    const c = preview.current;
    if (!c || !hasilUpload) return;
    c.width = hasilUpload.width;
    c.height = hasilUpload.height;
    c.getContext("2d").putImageData(new ImageData(hasilUpload.data, hasilUpload.width, hasilUpload.height), 0, 0);
  }, [hasilUpload]);

  const pilihFile = (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Pilih file gambar (JPG/PNG/WEBP)", { position: "bottom-right" });
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Ukuran foto maksimal 15 MB", { position: "bottom-right" });
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const skala = Math.min(1, MAKS_SISI_UPLOAD / Math.max(img.naturalWidth, img.naturalHeight));
      const w = Math.max(1, Math.round(img.naturalWidth * skala));
      const h = Math.max(1, Math.round(img.naturalHeight * skala));
      const c = document.createElement("canvas");
      c.width = w;
      c.height = h;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#fff"; // PNG transparan asli: anggap kertas putih
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      sumber.current = { data: ctx.getImageData(0, 0, w, h).data, w, h };
      URL.revokeObjectURL(url);
      setNamaFile(file.name);
      prosesUpload();
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      toast.error("File gambar tidak bisa dibaca", { position: "bottom-right" });
    };
    img.src = url;
  };

  const bisaSimpan = useMemo(
    () => (metode === "Gambar" ? adaGoresan : !!hasilUpload),
    [metode, adaGoresan, hasilUpload]
  );

  const simpan = async () => {
    setSaving(true);
    try {
      let blob = null;
      if (metode === "Gambar") {
        const c = papan.current;
        const ctx = c.getContext("2d");
        const img = ctx.getImageData(0, 0, c.width, c.height);
        const potong = potongRapat(img.data, c.width, c.height, 8);
        if (!potong) throw new Error("Belum ada tanda tangan di kanvas");
        blob = await keBlob(potong.data, potong.width, potong.height);
      } else {
        blob = await keBlob(hasilUpload.data, hasilUpload.width, hasilUpload.height);
      }
      await simpanTandaTangan(target.Nik, blob, metode);
      toast.success(`Tanda tangan ${target.NamaKaryawan} disimpan`, { position: "bottom-right", autoClose: 3000 });
      onSaved();
    } catch (e) {
      toast.error(e.message, { position: "bottom-right" });
    }
    setSaving(false);
  };

  const hapus = async () => {
    if (!window.confirm(`Hapus tanda tangan ${target.NamaKaryawan}?`)) return;
    try {
      await hapusTandaTangan(target.Nik);
      toast.success("Tanda tangan dihapus", { position: "bottom-right", autoClose: 3000 });
      onSaved();
    } catch (e) {
      toast.error(e.message, { position: "bottom-right" });
    }
  };

  const tab = (nilai, label) => (
    <button
      type="button"
      onClick={() => setMetode(nilai)}
      className={
        "px-4 py-2 text-sm font-semibold border-b-2 " +
        (metode === nilai ? "border-blue-500 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700")
      }
    >
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 overflow-y-auto py-10">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-3">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div>
            <div className="font-semibold text-gray-800">Tanda Tangan Digital</div>
            <div className="text-xs text-gray-500">
              {target.NamaKaryawan} · {target.Nik}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl px-2" aria-label="Tutup">
            ✕
          </button>
        </div>

        <div className="px-5 pt-4">
          {target.TandaTangan && (
            <div className="mb-4 border rounded p-3">
              <div className="text-xs font-bold uppercase text-gray-500 mb-2">
                Terpasang sekarang{target.TandaTanganTanggal ? ` · ${target.TandaTanganTanggal}` : ""}
              </div>
              <div className="rounded flex items-center justify-center" style={{ ...KOTAK, minHeight: 90 }}>
                {memuat ? (
                  <span className="text-xs text-gray-400">Memuat…</span>
                ) : saatIni ? (
                  <img src={saatIni} alt="Tanda tangan terpasang" style={{ maxHeight: 90, maxWidth: "100%" }} />
                ) : (
                  <span className="text-xs text-gray-400">Gambar tidak dapat dimuat</span>
                )}
              </div>
              <button type="button" onClick={hapus} className="mt-2 text-xs text-red-600 hover:underline">
                Hapus tanda tangan ini
              </button>
            </div>
          )}

          <div className="flex border-b mb-4">
            {tab("Gambar", "Tanda Tangan Langsung")}
            {tab("Upload", "Upload File")}
          </div>

          {metode === "Gambar" && (
            <div>
              <div className="text-xs text-gray-500 mb-2">Tanda tangani di kotak di bawah (mouse, layar sentuh, atau pena).</div>
              <div className="rounded border" style={KOTAK}>
                <canvas
                  ref={papan}
                  onPointerDown={mulai}
                  onPointerMove={gerak}
                  onPointerUp={selesai}
                  onPointerCancel={selesai}
                  onPointerLeave={selesai}
                  style={{ width: "100%", aspectRatio: `${PAPAN_W} / ${PAPAN_H}`, touchAction: "none", cursor: "crosshair", display: "block" }}
                />
              </div>
              <div className="flex items-center gap-3 mt-2 text-sm">
                <span className="text-xs text-gray-500">Warna pena:</span>
                {["hitam", "biru"].map((w) => (
                  <label key={w} className="flex items-center gap-1 capitalize">
                    <input type="radio" name="warnaPena" checked={warnaPena === w} onChange={() => setWarnaPena(w)} />
                    {w}
                  </label>
                ))}
                <button type="button" onClick={bersihkanPapan} className="ml-auto text-xs text-red-600 hover:underline">
                  Ulangi / hapus coretan
                </button>
              </div>
            </div>
          )}

          {metode === "Upload" && (
            <div>
              <div className="text-xs text-gray-500 mb-2">
                Foto atau scan tanda tangan di kertas putih/polos (JPG, PNG, WEBP). Background dihapus otomatis menjadi PNG transparan.
              </div>
              <input type="file" accept="image/*" onChange={(e) => pilihFile(e.target.files[0])} className="text-sm" />
              {namaFile && <div className="text-xs text-gray-400 mt-1">{namaFile}</div>}

              {sumber.current && (
                <>
                  <div className="mt-3 rounded border flex items-center justify-center p-2" style={{ ...KOTAK, minHeight: 120 }}>
                    {hasilUpload ? (
                      <canvas ref={preview} style={{ maxWidth: "100%", maxHeight: 180 }} />
                    ) : (
                      <span className="text-xs text-gray-400">Tidak ada hasil</span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 text-sm">
                    <div>
                      <div className="text-xs font-bold uppercase text-gray-500">Kepekaan ({kepekaan})</div>
                      <input type="range" min="0" max="100" value={kepekaan} onChange={(e) => setKepekaan(Number(e.target.value))} className="w-full" />
                      <div className="text-xs text-gray-400">Rendah = lebih bersih dari noda; tinggi = goresan tipis tetap ada.</div>
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase text-gray-500">Warna tinta</div>
                      <select value={warnaHasil} onChange={(e) => setWarnaHasil(e.target.value)} className="border rounded px-2 py-1 w-full">
                        <option value="asli">Sesuai aslinya</option>
                        <option value="hitam">Hitam</option>
                        <option value="biru">Biru tua</option>
                      </select>
                    </div>
                  </div>
                  {peringatan && <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">{peringatan}</div>}
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t mt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded">
            Batal
          </button>
          <button
            type="button"
            onClick={simpan}
            disabled={!bisaSimpan || saving}
            className="px-4 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 rounded disabled:opacity-50"
          >
            {saving ? "Menyimpan…" : "Simpan Tanda Tangan"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TandaTanganModal;
